import { Eye, Download, RotateCcw } from "lucide-react";
import { formatDate } from "../alivecor_reports/ecg_reports_utils";

/**
 * Reports table.
 *
 * Columns: SELECT ALL | REPORT NAME | PATIENT ID | TEST DATE | REPORT DATE | CUSTOMER | ACTIONS
 *
 * Per the latest request, the CUSTOMER column replaces LOCATION in the
 * table (location is no longer shown here at all — it was already removed
 * from the filter panel; now it's removed from the table too and replaced
 * with the report's customer name).
 */
export default function ECGReportsTable({
  reports,
  loading,
  error,
  onRetry,
  selectedReports,
  onToggleSelect,
  onToggleSelectAll,
  onView,
  onDownload,
  hasActiveFilters,
  onClearFilters,
}) {
  const pageIds = reports.map((r) => r.id);
  const allSelectedOnPage =
    pageIds.length > 0 && pageIds.every((id) => selectedReports.has(id));
  const someSelectedOnPage = pageIds.some((id) => selectedReports.has(id));

  const getReportName = (report) =>
    report.name || report.patient?.PatientName || "*";

  const getCustomerName = (report) => report.customer?.name || "*";

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="w-10 px-4 py-3 text-left">
              <input
                type="checkbox"
                checked={allSelectedOnPage}
                ref={(el) => {
                  if (el) el.indeterminate = !allSelectedOnPage && someSelectedOnPage;
                }}
                onChange={(e) => onToggleSelectAll(e.target.checked, pageIds)}
                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                aria-label="Select all reports on this page"
              />
            </th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Report Name</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Patient ID</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Test Date</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Report Date</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Customer</th>
            <th className="px-4 py-3 text-left font-medium text-gray-500">Actions</th>
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-100">
          {loading && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                Loading ECG reports...
              </td>
            </tr>
          )}

          {!loading && error && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center">
                <p className="mb-3 text-gray-600">Unable to load ECG reports.</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5
                             text-sm font-medium text-white hover:bg-red-700"
                >
                  <RotateCcw className="h-4 w-4" />
                  Retry
                </button>
              </td>
            </tr>
          )}

          {!loading && !error && reports.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-10 text-center">
                <p className="mb-3 text-gray-600">
                  {hasActiveFilters
                    ? "No ECG reports match your filters."
                    : "No ECG reports found"}
                </p>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={onClearFilters}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm
                               font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                )}
              </td>
            </tr>
          )}

          {!loading &&
            !error &&
            reports.map((report) => (
              <tr key={report.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedReports.has(report.id)}
                    onChange={(e) => onToggleSelect(report.id, e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    aria-label={`Select report ${getReportName(report)}`}
                  />
                </td>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {getReportName(report)}
                </td>
                <td className="px-4 py-3 text-gray-700">{report.patient_id ?? "*"}</td>
                <td className="px-4 py-3 text-gray-700">{formatDate(report.test_date)}</td>
                <td className="px-4 py-3 text-gray-700">{formatDate(report.report_date)}</td>
                <td className="px-4 py-3 text-gray-700">{getCustomerName(report)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => onView(report)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300
                                 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Eye className="h-3.5 w-3.5" />
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => onDownload(report)}
                      className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-2.5 py-1
                                 text-xs font-medium text-white hover:bg-red-700"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}